import type { OfferingV2Response, VariationView } from '../../types/offeringsV2';
import { FIXED_VALUES_MAX, setVariationValues, type PackageFormItem } from './packageUtils';
import type { PackagesT } from './packagesI18n';

export interface PackageChoiceFixerProps {
  t: PackagesT;
  lang: 'en' | 'ar';
  item: PackageFormItem;
  /** The service's catalogue entry; undefined when it is no longer listed. */
  offering: OfferingV2Response | undefined;
  name: string;
  disabled?: boolean;
  /** Row error (R3 ineligible, server items[i]). */
  error?: string;
  onChange: (next: PackageFormItem) => void;
}

const valueLabel = (v: VariationView['values'][number], lang: 'en' | 'ar') => (lang === 'ar' && v.labelAr ? v.labelAr : v.label);
const variationName = (v: VariationView, lang: 'en' | 'ar') => (lang === 'ar' && v.nameAr ? v.nameAr : v.name);

/**
 * Spec 081b — one row per variation of a picked service. SINGLE: a select with "Customer picks";
 * MULTI: checkboxes plus "Customer picks" (none fixed). Leaving any choice open, or a car-specific
 * service, makes the package PERCENT (D2).
 */
export default function PackageChoiceFixer(props: PackageChoiceFixerProps) {
  const { t, lang, item, offering, name, disabled, error, onChange } = props;
  const variations = [...(offering?.variations ?? [])].sort((a, b) => a.sortOrder - b.sortOrder);
  const fixed = new Set(item.fixedValueIds);
  const atMax = item.fixedValueIds.length >= FIXED_VALUES_MAX;
  const errId = `pkg-err-item-${item.offeringId}`;

  const setValues = (variationId: string, valueIds: string[]) => {
    if (offering) onChange(setVariationValues(item, offering, variationId, valueIds));
  };

  return (
    <fieldset className={`pkg-fixer${error ? ' pkg-fixer--error' : ''}`} disabled={disabled} data-testid={`pkg-fixer-${item.offeringId}`}
      aria-describedby={error ? errId : undefined}>
      <legend className="pkg-fixer__title">
        {t('packages.fixer.title', { name })}
        {offering?.isCarSpecific && <span className="pkg-badge pkg-badge--muted">{t('packages.fixer.carSpecific')}</span>}
      </legend>
      {offering?.isCarSpecific && <p className="offer-field-hint">{t('packages.fixer.carSpecificHint')}</p>}
      {offering && variations.length === 0 && <p className="offer-muted">{t('packages.fixer.noOptions')}</p>}

      {variations.map((v) => {
        const own = (v.values ?? []).filter((val) => fixed.has(val.id)).map((val) => val.id);
        const selectId = `pkg-var-${item.offeringId}-${v.id}`;
        if ((v.selectionMode ?? 'SINGLE') === 'SINGLE') {
          return (
            <div className="pkg-fixer__row" key={v.id}>
              <label htmlFor={selectId}>{variationName(v, lang)}</label>
              <select id={selectId} className="offer-select" value={own[0] ?? ''}
                onChange={(e) => setValues(v.id, e.target.value ? [e.target.value] : [])}>
                <option value="">{t('packages.fixer.customerPicks')}</option>
                {(v.values ?? []).map((val) => <option key={val.id} value={val.id}>{valueLabel(val, lang)}</option>)}
              </select>
            </div>
          );
        }
        return (
          <div className="pkg-fixer__row" key={v.id} role="group" aria-label={variationName(v, lang)}>
            <span className="pkg-fixer__label">{variationName(v, lang)}</span>
            <div className="pkg-fixer__multi">
              <label className="pkg-check">
                <input type="checkbox" checked={own.length === 0} onChange={() => setValues(v.id, [])} />
                {t('packages.fixer.customerPicks')}
              </label>
              {(v.values ?? []).map((val) => {
                const on = fixed.has(val.id);
                return (
                  <label className="pkg-check" key={val.id}>
                    <input type="checkbox" checked={on} disabled={!on && atMax}
                      onChange={() => setValues(v.id, on ? own.filter((id) => id !== val.id) : [...own, val.id])} />
                    {valueLabel(val, lang)}
                  </label>
                );
              })}
            </div>
            <p className="offer-field-hint">{atMax ? t('packages.fixer.maxValues') : t('packages.fixer.multiHint')}</p>
          </div>
        );
      })}
      {error && <div className="offer-field-error" id={errId} role="alert" data-testid={`pkg-item-error-${item.offeringId}`}>{error}</div>}
    </fieldset>
  );
}
